/**
 * Desktop menu behaviour: hover-to-switch, and third-level flyouts.
 *
 * Dawn opens dropdowns on click and expands third-level items downward inside
 * the dropdown. Two additions here, both desktop-only and both gated on a real
 * hovering pointer so touch is untouched:
 *
 *   1. Once a menu is open, hovering a sibling switches to it. Intent has
 *      already been expressed by the first click, so demanding another click
 *      per menu is friction. Hovering with nothing open does NOT open anything,
 *      which is what makes accidental opens the usual complaint about hover
 *      menus.
 *
 *   2. A third-level item opens to the side on hover rather than pushing the
 *      dropdown taller, and flips to the other side when it would otherwise run
 *      off the edge of the window.
 *
 * Short delays on both: a pointer crossing a menu on its way elsewhere should
 * not set it flashing.
 */
(function () {
  const OPEN_DELAY = 70;
  const CLOSE_DELAY = 180;

  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  function topLevelDetails(nav) {
    return Array.from(nav.querySelectorAll(':scope > ul > li > header-menu > details'));
  }

  function anyOpen(list) {
    return list.some((details) => details.open);
  }

  function setupHoverSwitch(nav) {
    if (nav.dataset.hoverSwitch === 'true') return;
    nav.dataset.hoverSwitch = 'true';

    let timer = null;

    nav.addEventListener('mouseover', (event) => {
      const summary = event.target.closest('summary');
      if (!summary) return;

      const details = summary.parentElement;
      const siblings = topLevelDetails(nav);
      if (!siblings.includes(details)) return;

      // Only switch between menus; never open the first one on hover.
      if (!anyOpen(siblings) || details.open) return;

      clearTimeout(timer);
      timer = setTimeout(() => {
        siblings.forEach((other) => {
          if (other === details) return;
          // Dawn's own close() also resets aria-expanded; setting .open alone
          // leaves the summary claiming to be expanded.
          const disclosure = other.parentElement;
          if (disclosure && typeof disclosure.close === 'function') disclosure.close();
          else other.open = false;
        });

        details.open = true;

        const summaryToFocus = details.querySelector('summary');
        if (!summaryToFocus) return;
        summaryToFocus.setAttribute('aria-expanded', 'true');

        // Focus has to follow, not just for the keyboard. Dawn closes a menu
        // from focusout, so a menu opened by hover while focus sat on the
        // summary that was clicked first never receives the event that would
        // close it: clicking elsewhere on the page did nothing and the only way
        // out was clicking its own caret. Escape is focus-based too.
        summaryToFocus.focus({ preventScroll: true });
      }, OPEN_DELAY);
    });

    nav.addEventListener('mouseleave', () => clearTimeout(timer));
  }

  /* Third level: open to the side on hover, flipping when there is no room. */
  function setupFlyouts(nav) {
    nav.querySelectorAll('.header__submenu li > details').forEach((details) => {
      const item = details.parentElement;
      if (item.dataset.flyout === 'true') return;
      item.dataset.flyout = 'true';

      let openTimer = null;
      let closeTimer = null;

      item.addEventListener('mouseenter', () => {
        clearTimeout(closeTimer);
        openTimer = setTimeout(() => {
          details.open = true;
          positionFlyout(details);
        }, OPEN_DELAY);
      });

      item.addEventListener('mouseleave', () => {
        clearTimeout(openTimer);
        closeTimer = setTimeout(() => {
          details.open = false;
        }, CLOSE_DELAY);
      });
    });
  }

  function positionFlyout(details) {
    const panel = details.querySelector('.header__submenu');
    if (!panel) return;

    panel.classList.remove('header__submenu--flip');
    const rect = panel.getBoundingClientRect();
    if (rect.right > document.documentElement.clientWidth - 8) {
      panel.classList.add('header__submenu--flip');
    }
  }

  /**
   * Holds the sticky header in place while a menu is open.
   *
   * Dawn hides the header when scrolling down and, in doing so, calls
   * closeMenuDisclosure() -- which shuts any open menu. A submenu long enough
   * to need scrolling is therefore impossible to reach the bottom of: the act
   * of scrolling towards it closes it.
   *
   * StickyHeader already checks a preventHide flag before hiding and nothing
   * ever sets it, so this uses that hook rather than reaching into its
   * internals.
   */
  function setupStickyHold(nav) {
    if (nav.dataset.stickyHold === 'true') return;
    nav.dataset.stickyHold = 'true';

    const sync = () => {
      const sticky = document.querySelector('sticky-header');
      if (!sticky) return;
      sticky.preventHide = nav.querySelector('details[open]') !== null;
    };

    // Dawn's HeaderMenu.onToggle sets this same flag, but from one menu's own
    // open state, so hover-switching can leave it false while another menu is
    // open: the close and the open both fire and whichever lands last wins.
    // Reading the whole nav instead is order-independent -- deferred by a frame
    // so it settles after Dawn's handler and after both details have changed.
    //
    // toggle does not bubble, so it is caught on the way down instead.
    nav.addEventListener('toggle', () => requestAnimationFrame(sync), true);
  }

  function init() {
    document.querySelectorAll('.header__inline-menu').forEach((nav) => {
      setupHoverSwitch(nav);
      setupFlyouts(nav);
      setupStickyHold(nav);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
